/**
 * @file Transactions : operations economiques composees (acheter = debiter + effet, accorder un RewardBundle complet, gains scales par stats).
 */
import { Decimal } from '@shared/bignum';
import type { WalletId } from '@shared/enums';
import type { Result } from '@shared/types';
import type { RewardBundle, WalletAmounts } from '@config/types';
import type { SimContext } from '../Context';
import { resolveAmounts } from './Wallet';
import type { ResolvedAmounts } from './Wallet';

/** Stat multiplicatrice appliquee a chaque monnaie gagnee en jeu (ex. money -> stat `money`). */
const GAIN_STAT: Partial<Record<WalletId, 'money' | 'diaGain' | 'petCoinGain' | 'harvestYield'>> = {
  money: 'money',
  dia: 'diaGain',
  petCoin: 'petCoinGain',
  wood: 'harvestYield',
  stone: 'harvestYield',
  ironOre: 'harvestYield',
  copperOre: 'harvestYield',
  silverOre: 'harvestYield',
  goldOre: 'harvestYield',
  crystal: 'harvestYield',
};

export class Transactions {
  constructor(private readonly ctx: SimContext) {}

  /**
   * Achat atomique : verifie le cout, execute `effect` ; si l'effet echoue, rien n'est debite.
   * `effect` ne doit PAS lui-meme toucher au wallet pour le cout.
   */
  purchase<T>(cost: WalletAmounts | ResolvedAmounts, reason: string, effect: () => Result<T>): Result<T> {
    const resolved = this.isResolved(cost) ? cost : resolveAmounts(cost);
    if (!this.ctx.wallet.canAfford(resolved)) return { ok: false, error: 'notEnoughFunds' };
    const r = effect();
    if (!r.ok) return r;
    this.ctx.wallet.spend(resolved, reason);
    return r;
  }

  /**
   * Gain de jeu (kill, fin de mode) : applique la stat de gain associee (money, diaGain...) puis credite.
   * Cumule l'argent dans run.moneyAccum (base du Rebirth). Retourne les montants reellement credites.
   */
  earn(amounts: ResolvedAmounts, reason: string): ResolvedAmounts {
    const out: ResolvedAmounts = {};
    for (const [id, v] of Object.entries(amounts) as Array<[WalletId, Decimal]>) {
      const stat = GAIN_STAT[id];
      const final = stat ? v.times(this.ctx.stats.get(stat, { mode: this.ctx.modes.currentId })) : v;
      out[id] = final;
    }
    this.ctx.wallet.grant(out, reason);
    const money = out.money;
    if (money && money.gt(0)) {
      this.ctx.data.run.moneyAccum = this.ctx.data.run.moneyAccum.plus(money);
      this.ctx.services.missions.increment('moneyEarned', money.toNumber());
    }
    return out;
  }

  /** Accorde un lot de recompenses (missions, passe, cadeaux, fin de mode). Montants NON scales par les stats. */
  grantBundle(bundle: RewardBundle, reason: string): void {
    if (bundle.wallet) {
      const amounts = resolveAmounts(bundle.wallet);
      this.ctx.wallet.grant(amounts, reason);
      const m = amounts.money;
      if (m) this.ctx.data.run.moneyAccum = this.ctx.data.run.moneyAccum.plus(m);
    }
    if (bundle.tickets) this.ctx.services.tickets.grant(bundle.tickets);
    if (bundle.passXp) this.ctx.services.pass.addXp(bundle.passXp);
    if (bundle.buffs) for (const b of bundle.buffs) this.ctx.services.buffs.addSeconds(b.id, b.seconds, false);
    if (bundle.draws) for (const d of bundle.draws) this.ctx.services.weapons.drawFree(d.pool, d.count);
    if (bundle.weapon) this.ctx.services.weapons.grantWeapon(bundle.weapon.defId, reason);
    if (bundle.skinId) this.ctx.services.skins.grant(bundle.skinId);
    this.ctx.bus.emit('RewardShown', { bundle });
  }

  private isResolved(c: WalletAmounts | ResolvedAmounts): c is ResolvedAmounts {
    return Object.values(c).every((v) => v instanceof Decimal);
  }
}
